import React, { useState, useEffect } from "react";
import { Modal, message } from "antd";
import { renderGrid } from "../common/FormRenderer";
import {
  PQS_PICKLIST_LABELS,
  buildCreateFieldsForPqs,
} from "./projectQualStepColumnsConfig";
import { validateProjectQualStepEditForm } from "../../config/projectQualStepEdit.validation";
import { usePicklistMap, resolvePicklistFields } from "../../hooks/usePicklist";
import { SOBJECT_PROJECT_QUAL_STEP } from "../../config/sobjectNames";

export default function CreateQualificationStepModal({
  visible,
  onClose,
  onSuccess,
  initialValues,
}) {
  const { picklistMap } = usePicklistMap(
    Object.values(PQS_PICKLIST_LABELS),
    SOBJECT_PROJECT_QUAL_STEP,
  );

  const [form, setForm] = useState({
    name: "",
    project: "",
    objective: "",
    status: "Draft",
    qualificationStep: "",
    funnel: "A",
    stepNumber: "",
    numberOfAttempts: "1",
  });
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [isInitialEmpty, setIsInitialEmpty] = useState(false);

  // Apply incoming initial values on open (for clone -> create flow)
  useEffect(() => {
    if (initialValues && Object.keys(initialValues).length === 0) {
      setIsInitialEmpty(true);
    }
    if (initialValues !== undefined) {
      const { projectId, projectName } = initialValues;
      if (projectId) {
        initialValues = {
          project: projectName,
          projectId: projectId,
          ...initialValues,
        };
      }
    }
    if (visible && initialValues && typeof initialValues === "object") {
      setForm((prev) => ({ ...prev, ...initialValues }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, initialValues]);

  const handleSave = async () => {
    // Validate form before saving
    const validationPayload = {
      info: {
        projectId: form.projectId || form.project,
        qualificationStepId: form.qualificationStepId || form.qualificationStep,
        funnel: form.funnel,
        stepNumber: form.stepNumber,
        numberOfAttempts: form.numberOfAttempts,
        estimatedTimeMinutes: form.estimatedTimeMinutes || "",
      },
    };

    const errors = validateProjectQualStepEditForm(validationPayload);

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    setValidationErrors({});
    // Define the actual save operation so we can optionally gate it behind a warning
    const doSave = async () => {
      setSaving(true);
      try {
        // If both Project and Project Objective are selected, ensure they belong together
        if (form.projectId && form.objectiveId) {
          try {
            const { fetchProjectObjective } =
              await import("../../services/projectObjectiveService");
            const po = await fetchProjectObjective(form.objectiveId);
            const poProjectId = po?.info?.projectId || po?.projectId || "";
            if (poProjectId && String(poProjectId) !== String(form.projectId)) {
              message.error(
                "Selected Project Objective does not belong to the selected Project. Please choose a matching objective.",
              );
              setSaving(false);
              return;
            }
          } catch (prefetchErr) {
            // If we cannot verify, proceed but log for debugging
            // eslint-disable-next-line no-console
            console.warn(
              "Could not verify Project Objective project ownership:",
              prefetchErr,
            );
          }
        }
        // Import the service dynamically
        const { createIndividualQualificationStep } =
          await import("../../services/projectQualStepService");

        const result = await createIndividualQualificationStep(form);

        // Treat success for any of these shapes:
        // 1) [{ Id: \"...\" }]  2) { Id: \"...\" }  3) { data: [{ Id: \"...\" }] }  4) { data: { Id: \"...\" } }
        const created =
          (Array.isArray(result) && result.length > 0 && result[0]?.Id) ||
          (result && result.Id) ||
          (result && Array.isArray(result.data) && result.data[0]?.Id) ||
          (result && result.data && result.data.Id);

        if (created) {
          message.success("Qualification Step created successfully!");
          // Reset form
          setForm({
            name: "",
            project: "",
            objective: "",
            status: "Draft",
            qualificationStep: "",
            funnel: "A",
            stepNumber: "",
            numberOfAttempts: "1",
          });
          onSuccess();
        } else {
          message.error(
            result?.error?.message || "Failed to create Qualification Step",
          );
        }
      } catch (error) {
        console.error("Error creating qualification step:", error);
        message.error(
          error.message ||
            "An error occurred while creating the Qualification Step",
        );
      } finally {
        setSaving(false);
      }
    };

    const attempts = Number((form?.numberOfAttempts || "").toString());
    if (Number.isFinite(attempts) && attempts > 1) {
      Modal.confirm({
        title: "Multiple Attempts Warning",
        content:
          "Number of Attempts is greater than 1. Most Qualification Steps allow only one attempt by default. Ensure the selected Qualification Step supports multiple attempts, or reduce this to 1 to avoid Salesforce errors.",
        okText: "Continue",
        cancelText: "Go Back",
        onOk: async () => {
          await doSave();
        },
      });
      return;
    }

    await doSave();
  };

  // Note: Project prefill is handled in the wizard flow (step component).
  // This standalone modal intentionally does not auto-fill project context.

  const handleCancel = () => {
    setForm({
      name: "",
      project: "",
      objective: "",
      status: "Draft",
      qualificationStep: "",
      funnel: "A",
      stepNumber: "",
      numberOfAttempts: "1",
    });
    setValidationErrors({});
    onClose();
  };

  const handleChange = (path, value, displayName) => {
    if (Object.keys(validationErrors).length > 0) {
      setValidationErrors({});
    }
    setForm((prev) => {
      const update = { ...prev, [path]: value };
      if (displayName !== undefined) {
        update[`${path}DisplayName`] = displayName;
      }
      return update;
    });
  };
  const fields = resolvePicklistFields(buildCreateFieldsForPqs(), picklistMap);

  // Get validation error messages directly from centralized validation
  const errorMessages = Object.values(validationErrors);

  return (
    <Modal
      title="Create Qualification Step"
      open={visible}
      onOk={handleSave}
      onCancel={handleCancel}
      okText="Save"
      cancelText="Cancel"
      confirmLoading={saving}
      width={700}
      bodyStyle={{ maxHeight: "70vh", overflowY: "auto", padding: 0 }}
    >
      {/* Validation Error Banner */}
      {errorMessages.length > 0 && (
        <div className="sticky top-0 z-10 px-6 py-3 bg-red-50 border-b border-red-200">
          <div className="flex items-start gap-3">
            <svg
              className="w-5 h-5 flex-shrink-0 text-red-600 mt-0.5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-semibold text-red-800 mb-1.5">
                {errorMessages.length} error
                {errorMessages.length !== 1 ? "s" : ""} found:
              </h4>
              <div className="max-h-24 overflow-y-auto pr-2">
                <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
                  {errorMessages.map((error, index) => (
                    <li key={index} className="leading-relaxed">
                      {error}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Form Content */}
      <div className="space-y-4 p-6">
        {renderGrid(fields, {
          formData: form,
          onChange: handleChange,
          showValidation: true,
          searchParams: isInitialEmpty
            ? { initialValues: { projectId: form?.project } }
            : { initialValues },
        })}
      </div>
    </Modal>
  );
}
